import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'API';
import {
    EmailMessagePayloadWithAttachments,
    NewCommentPayload,
    SetSharedClosedReportDataProps,
    SupportRequest,
} from 'core/entities';

export const getOfflineReports = createAsyncThunk(
    'offline-reports/get-offline-reports',
    async () => {
        try {
            const data = await API.support.getOfflineReports();

            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportsSharedWithMe = createAsyncThunk(
    'offline-reports/get-offline-reports-shared-with-me',
    async () => {
        try {
            const data = await API.support.getOfflineReportsSharedWithMe();

            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportsAccount = createAsyncThunk(
    'offline-reports/get-offline-reports-account',
    async () => {
        try {
            const data = await API.support.getOfflineReportsAccount();

            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportsUserAccountInfo = createAsyncThunk(
    'offline-reports/get-offline-reports-user-account-info',
    async () => {
        try {
            const data = await API.support.getOfflineReportsUserAccountInfo();

            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportDetails = createAsyncThunk(
    'offline-reports/get-offline-report-details',
    async (id: string) => {
        try {
            const data = await API.support.getOfflineReportDetails(id);
            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportsConfigurations = createAsyncThunk(
    'offline-reports/get-offline-reports-configurations',
    async () => {
        try {
            const data = await API.support.getOfflineReportsConfigurations();
            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportsRequests = createAsyncThunk(
    'offline-reports/get-offline-reports-requests',
    async () => {
        try {
            const data = await API.support.getOfflineReportsRequests();
            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportsRequestsComments = createAsyncThunk(
    'offline-reports/get-offline-reports-requests-comments',
    async ({ requestId }: { requestId: string }) => {
        try {
            return await API.support.getOfflineReportsRequestsComments(requestId);
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const postOfflineReportsRequestsComment = createAsyncThunk(
    'offline-reports/post-offline-reports-requests-comment',
    async ({ requestId, payload }: { requestId: string; payload: NewCommentPayload }) => {
        try {
            const data = await API.support.postOfflineReportsComment(requestId, payload);
            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const postCaseEmailMsg = createAsyncThunk(
    'offline-reports/post-case-email-msg',
    async ({ payload }: { payload: EmailMessagePayloadWithAttachments }, { rejectWithValue }) => {
        try {
            return await API.support.postEmailMsg(payload);
        } catch (error) {
            return rejectWithValue('Failed to post email message');
        }
    },
);

export const updateOfflineReportsOrderRequestReadReceipt = createAsyncThunk(
    'offline-reports/update-offline-reports-order-request-read-receipt',
    async ({ requestId, flag }: { requestId: string; flag: boolean }) => {
        try {
            const data = await API.support.updateReadReceipt(requestId, flag);
            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const getOfflineReportsCreditsBalance = createAsyncThunk(
    'offline-reports/get-offline-reports-credits-balance',
    async () => {
        try {
            const data = await API.support.getCreditsBalance();
            return data;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const clearOfflineReportsRequestComments = createAction(
    'offline-reports/post-offline-reports-requests-clear-comments',
);

export const clearOfflineReportsSubmitStatus = createAction(
    'offline-reports/submit-offline-report-request-clear-submit-status',
);

export const openInsufficientCreditsPopup = createAction(
    'offline-reports/open-insufficient-credits-popup',
);

const setIsSubmitLoadingPopupVisibleAction = 'offline-reports/set-is-submit-loading-popup-visible';
export const setIsSubmitLoadingPopupVisible = createAction<boolean>(
    setIsSubmitLoadingPopupVisibleAction,
);

const setSharedDataClosedReportsAction = 'offline-reports/set-shared-data-closed-reports';
export const updateClosedReportSharedAndTargetValues = createAction<SetSharedClosedReportDataProps>(
    setSharedDataClosedReportsAction,
);

const clearReportDetailsAction = 'offline-reports/clear-report-details';
export const clearReportDetails = createAction(clearReportDetailsAction);

export const getSupportRequests = createAsyncThunk(
    'offline-reports/get-support-requests',
    async () => {
        try {
            const data = await API.support.getSupportRequests();

            const updatedData = {
                ...data,
                requests: data.requests.map((request) => ({
                    ...request,
                    subject: request.subject?.replace('ASAQ - ', ''),
                })),
            };

            return updatedData;
        } catch (error) {
            console.error('error', error);
        }
    },
);

export const setSupportStatuses = createAction<string[]>('offline-reports/set-support-statuses');

export const setSupportTypes = createAction<string[]>('offline-reports/set-support-types');

export const setSupportSearchText = createAction<string>('offline-reports/set-support-search-text');

export const setSupportSelectedRequest = createAction<SupportRequest | undefined>(
    'offline-reports/set-support-selected-request',
);

export const setSupportRequestsLoading = createAction<boolean>(
    'offline-reports/set-support-requests-loading',
);
